/**
 * DHIS2-style Gauge Chart Component
 * Displays a single value within a range with color-coded thresholds
 */
import React from 'react';
import { Box, Card, CardContent, Typography } from '@mui/material';
import { PieChart, Pie, Cell } from 'recharts';
import { formatNumber } from '../../theme/dashboardTheme';

interface GaugeChartProps {
  title: string;
  value: number;
  min?: number;
  max?: number;
  thresholds?: Array<{ value: number; color: string; label: string }>;
  format?: 'integer' | 'percentage' | 'ratio' | 'decimal';
  unit?: string;
  subtitle?: string;
  height?: number;
}

const GaugeChart: React.FC<GaugeChartProps> = ({
  title,
  value,
  min = 0,
  max = 100,
  thresholds = [
    { value: 30, color: '#D32F2F', label: 'Critical' },
    { value: 50, color: '#FF9302', label: 'Below Average' },
    { value: 70, color: '#FFC324', label: 'Average' },
    { value: 90, color: '#4CAF50', label: 'Good' },
    { value: 100, color: '#388E3C', label: 'Excellent' },
  ],
  format = 'integer',
  unit,
  subtitle,
  height = 200,
}) => {
  const range = max - min;
  const normalizedValue = Math.min(Math.max(value - min, 0), range);
  const percentage = (normalizedValue / range) * 100;

  // Build gauge segments from thresholds
  const segments: Array<{ value: number; color: string }> = [];
  let prevValue = 0;
  for (const threshold of thresholds) {
    const segmentSize = threshold.value - prevValue;
    if (segmentSize > 0) {
      segments.push({ value: segmentSize, color: threshold.color });
    }
    prevValue = threshold.value;
  }

  // Calculate needle angle (180 degree arc)
  const needleAngle = 180 - (percentage / 100) * 180;

  // Current color based on value
  const currentColor = (() => {
    for (const threshold of thresholds) {
      if (percentage <= threshold.value) return threshold.color;
    }
    return thresholds[thresholds.length - 1]?.color || '#A0ADBA';
  })();

  const RADIAN = Math.PI / 180;
  const cx = 120;
  const cy = 110;
  const innerRadius = 60;
  const outerRadius = 90;

  // Needle rendering
  const renderNeedle = () => {
    const angle = RADIAN * (180 - (percentage / 100) * 180);
    const needleLength = outerRadius - 10;
    const x2 = cx + needleLength * Math.cos(angle);
    const y2 = cy - needleLength * Math.sin(angle);

    return (
      <g>
        <circle cx={cx} cy={cy} r={6} fill={currentColor} stroke="#fff" strokeWidth={2} />
        <line
          x1={cx} y1={cy} x2={x2} y2={y2}
          stroke={currentColor} strokeWidth={3} strokeLinecap="round"
        />
      </g>
    );
  };

  return (
    <Card sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          {title}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <PieChart width={240} height={130}>
            <Pie
              data={segments}
              cx={cx}
              cy={cy}
              startAngle={180}
              endAngle={0}
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              dataKey="value"
              stroke="none"
            >
              {segments.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            {renderNeedle()}
          </PieChart>
        </Box>

        <Typography variant="h4" sx={{ fontWeight: 700, color: currentColor, mt: -1 }}>
          {formatNumber(value, format)}
          {unit && <Typography component="span" variant="body2" sx={{ ml: 0.5, color: 'text.secondary' }}>{unit}</Typography>}
        </Typography>

        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{subtitle}</Typography>
        )}

        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
          {thresholds.map((t, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: t.color }} />
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>{t.label}</Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default GaugeChart;
